import React, { useMemo, useState } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './CollapsibleSection.css';
import { useProject } from '../contexts/ProjectContext';
import { trackEvent } from '../utils/analytics';

export interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  className?: string;
  contentClassName?: string;
  headerAriaLabelExpand?: string;
  headerAriaLabelCollapse?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultCollapsed = false,
  isCollapsed,
  onCollapsedChange,
  className,
  contentClassName,
  headerAriaLabelExpand = 'Expand',
  headerAriaLabelCollapse = 'Collapse',
}) => {
  const { selectedProject } = useProject();
  const isControlled: boolean = isCollapsed !== undefined;
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState<boolean>(defaultCollapsed);

  const collapsed: boolean = isControlled ? (isCollapsed as boolean) : uncontrolledCollapsed;

  const ariaLabel: string = useMemo(
    () => (collapsed ? headerAriaLabelExpand : headerAriaLabelCollapse),
    [collapsed, headerAriaLabelCollapse, headerAriaLabelExpand]
  );

  const setCollapsed = (next: boolean): void => {
    trackEvent('settings_section_toggled', {
      project_id: selectedProject?.id,
      section_title: title,
      next_collapsed: next,
    }, {
      dedupeKey: `${selectedProject?.id ?? 'no_project'}:${title}:${next}`,
      dedupeMs: 500,
    });
    onCollapsedChange?.(next);
    if (!isControlled) {
      setUncontrolledCollapsed(next);
    }
  };

  const toggle = (): void => setCollapsed(!collapsed);

  return (
    <div className={className}>
      <div
        className="settings-header"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="settings-header-title">{title}</span>
        <button
          type="button"
          className="settings-header-chevron"
          aria-label={ariaLabel}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <KeyboardArrowUpIcon className={`chevron-icon ${collapsed ? '' : 'expanded'}`} />
        </button>
      </div>

      {!collapsed ? <div className={contentClassName}>{children}</div> : null}
    </div>
  );
};


